/**
 * ProfileDataGrid Component
 *
 * Displays profile data in a clean two-column grid layout with horizontal dividers
 * Left: Label (bold, muted) with optional icon
 * Right: Value (normal weight)
 * Features: Horizontal separators between sections, responsive grid, shadcn styling
 */

import React from 'react';
import { Separator } from '@/components/ui/separator';

export interface DataGridItem {
  label: string;
  value?: string | number | boolean | null;
  icon?: React.ReactNode;
  format?: (value: any) => string;
}

interface ProfileDataGridProps {
  title?: string;
  subtitle?: string;
  items: DataGridItem[];
  columns?: 1 | 2 | 3;
}

export const ProfileDataGrid: React.FC<ProfileDataGridProps> = ({
  title,
  subtitle,
  items,
  columns = 2,
}) => {
  const formatValue = (item: DataGridItem): string => {
    if (item.value === null || item.value === undefined) {
      return '—';
    }

    if (item.format) {
      return item.format(item.value);
    }

    if (typeof item.value === 'boolean') {
      return item.value ? 'Yes' : 'No';
    }

    return String(item.value);
  };

  const gridColsClass = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
  }[columns];

  return (
    <div className="w-full h-full flex flex-col">
      {/* Header with separator */}
      {title && (
        <>
          <div className="mb-6 flex-shrink-0">
            <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
            {subtitle && <p className="text-sm text-gray-600">{subtitle}</p>}
          </div>
          <Separator className="mb-6" />
        </>
      )}

      {/* Grid with row separators - Expands to fill available space */}
      <div className={`flex-1 auto-rows-min`}>
        <div className={`grid ${gridColsClass} gap-6 gap-y-6`}>
          {items.map((item, idx) => (
            <div key={idx} className="flex flex-col space-y-3">
              {/* Label with optional icon */}
              <div className="flex items-center gap-3">
                {item.icon && (
                  <span className="text-blue-600 flex-shrink-0">
                    {item.icon}
                  </span>
                )}
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {item.label}
                </label>
              </div>

              {/* Value */}
              <p className="text-base font-medium text-gray-900 ml-0">
                {formatValue(item)}
              </p>

              {/* Horizontal divider */}
              <Separator className="mt-2" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProfileDataGrid;
